import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { default as Picker, registerLocale } from 'react-datepicker';
import { ru } from 'date-fns/locale/ru';
import { cn } from '@shared/utils';
import { Select } from '@shared/widgets';

registerLocale('ru', ru);

interface DateRangePickerInputProps {
  className?: string;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  readOnly?: boolean;
  dateFrom?: Dayjs;
  dateTo?: Dayjs;
  onChange?: (dateFrom?: Dayjs, dateTo?: Dayjs) => void;
}

export const DateRangePickerInput = (props: DateRangePickerInputProps) => {
  const [viewDate, setViewDate] = useState<Dayjs>(() => {
    const d = props.dateFrom;
    return (d?.isValid() ? d : dayjs()).startOf('month');
  });
  const month = viewDate.month();
  const year = viewDate.year();

  const { i18n } = useTranslation();

  const yearOptions = useMemo(() => {
    const nowYear = dayjs().year();
    const minYear = props.minDate?.isValid() ? props.minDate.year() : undefined;
    const maxYear = props.maxDate?.isValid() ? props.maxDate.year() : undefined;

    let startYear: number;
    let endYear: number;

    if (minYear != null && maxYear != null) {
      startYear = minYear;
      endYear = maxYear;
    } else if (minYear != null) {
      startYear = minYear;
      endYear = maxYear ?? nowYear + 6;
    } else if (maxYear != null) {
      endYear = maxYear;
      startYear = minYear ?? maxYear - 11;
    } else {
      startYear = nowYear - 5;
      endYear = nowYear + 6;
    }

    if (startYear > endYear) [startYear, endYear] = [endYear, startYear];

    return Array.from({ length: endYear - startYear + 1 }, (_, idx) => {
      const yearOption = startYear + idx;
      return { label: yearOption.toString(), value: yearOption };
    });
  }, [props.minDate, props.maxDate]);

  const monthOptions = useMemo(() => {
    const hasMin = props.minDate?.isValid();
    const hasMax = props.maxDate?.isValid();
    let minM = 0;
    let maxM = 11;
    if (hasMin && year === props.minDate!.year()) minM = props.minDate!.month();
    if (hasMax && year === props.maxDate!.year()) maxM = props.maxDate!.month();
    if (minM > maxM) [minM, maxM] = [maxM, minM];

    const locale = i18n.language || 'en';
    return Array.from({ length: maxM - minM + 1 }, (_, i) => {
      const idx = minM + i;
      return { label: dayjs().locale(locale).month(idx).format('MMMM'), value: idx };
    });
  }, [props.minDate, props.maxDate, year, i18n.language]);

  useEffect(() => {
    if (yearOptions.length === 0) return;
    const lo = yearOptions[0]!.value;
    const hi = yearOptions[yearOptions.length - 1]!.value;
    const y = viewDate.year();
    const clamped = Math.min(Math.max(y, lo), hi);
    if (clamped !== y) setViewDate((v) => v.year(clamped));
  }, [yearOptions, viewDate]);

  useEffect(() => {
    if (monthOptions.length === 0) return;
    const lo = monthOptions[0]!.value;
    const hi = monthOptions[monthOptions.length - 1]!.value;
    const m = viewDate.month();
    const clamped = Math.min(Math.max(m, lo), hi);
    if (clamped !== m) setViewDate((v) => v.month(clamped));
  }, [monthOptions, viewDate]);

  return (
    <div className={cn('flex flex-col justify-center', props.className)}>
      <div className='mb-2.5 flex items-center gap-4'>
        <Select
          className='min-w-[140px]'
          items={monthOptions}
          readOnly={props.readOnly}
          value={month}
          onChange={(value) => setViewDate((v) => v.month(value!))}
        />
        <Select
          className='min-w-[100px]'
          items={yearOptions}
          readOnly={props.readOnly}
          value={year}
          onChange={(value) => setViewDate((v) => v.year(value!))}
        />
      </div>
      <Picker
        inline
        selectsRange
        locale={i18n.language || 'en'}
        minDate={props.minDate?.toDate()}
        maxDate={props.maxDate?.toDate()}
        openToDate={viewDate.toDate()}
        readOnly={props.readOnly}
        startDate={props.dateFrom?.toDate()}
        endDate={props.dateTo?.toDate()}
        onChange={(dates) => {
          if (props.readOnly) return;

          const startDate = dates[0];
          const endDate = dates[1];
          const dateFrom = startDate != null ? dayjs(startDate) : undefined;
          const dateTo = endDate != null ? dayjs(endDate) : undefined;

          props.onChange?.(dateFrom, dateTo);
        }}
        onMonthChange={(newMonth) => setViewDate(dayjs(newMonth).startOf('month'))}
      />
    </div>
  );
};
